import React, { useMemo, useState } from "react";

import { matchSorter } from "match-sorter";

import { UserRole, type GroupUser } from "@eisox/api/core-api";
import { ActionButton, Button, cn, SearchField, Switch, TableV2 as Table, Typography } from "@eisox/design-system";
import { ChevronDownIcon, ChevronUpIcon, PencilIcon, PlusIcon } from "@eisox/icons";
import { colors } from "@eisox/tokens";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";

import { groupApi } from "~/api";
import { DeleteUserDialog, ModifyAdministratorDialog, UserDialog } from "~/features/groups";
import { RoleButton } from "~/features/users";
import { m } from "~/paraglide/messages";
import { useGroupContext } from "~/providers/group.provider";

export const Route = createFileRoute("/_authenticated/_no-building/groups/$groupId/users")({
  loader: async ({ context: { queryClient }, params: { groupId } }) => {
    await queryClient.ensureQueryData(groupApi.queries.users(groupId));
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { groupId } = Route.useParams();
  const { group } = useGroupContext(Route.id);
  const { data: users } = useSuspenseQuery(groupApi.queries.users(groupId));

  const eisox = !!group.root;

  const [search, setSearch] = useState("");

  const filteredUsers = useMemo(
    () => matchSorter(users, search, { keys: ["firstName", "lastName", "email"] }),
    [users, search],
  );

  const columns = useMemo<ColumnDef<GroupUser>[]>(
    () => [
      {
        accessorKey: "firstName",
        header: m.first_name(),
        cell: ({ getValue }) => <Typography>{getValue<GroupUser["firstName"]>()}</Typography>,
      },
      {
        accessorKey: "lastName",
        header: m.last_name(),
        cell: ({ getValue }) => <Typography>{getValue<GroupUser["lastName"]>()}</Typography>,
      },
      {
        accessorKey: "email",
        header: m.email(),
        cell: ({ getValue }) => <Typography>{getValue<GroupUser["email"]>()}</Typography>,
      },
      {
        accessorKey: "administrator",
        header: m.owner(),
        cell: ({ row }) =>
          import.meta.env.VITE_GROUPS_ACTIONS_ENABLED ? (
            <ModifyAdministratorDialog
              type={row.original.administrator ? "remove" : "add"}
              groupId={groupId}
              groupName={group.name}
              user={row.original}
            />
          ) : (
            <Switch checked={row.original.administrator} disabled />
          ),
      },
      {
        id: "roles",
        header: m.building_role(),
        cell: ({ row }) =>
          eisox ? (
            <RoleButton role={UserRole.INSTALLER}>{m.installer()}</RoleButton>
          ) : row.original.administrator ? (
            <RoleButton role={UserRole.OWNER}>{m.owner()}</RoleButton>
          ) : import.meta.env.VITE_GROUPS_ACTIONS_ENABLED ? (
            <UserDialog group={group} user={row.original}>
              <ActionButton style={{ backgroundColor: colors.primary.gray, color: colors.primary.darkBlue }}>
                {m.edit()} <PencilIcon />
              </ActionButton>
            </UserDialog>
          ) : null,
      },
      {
        id: "delete",
        cell: ({ row }) =>
          import.meta.env.VITE_GROUPS_ACTIONS_ENABLED ? (
            <DeleteUserDialog groupId={groupId} user={row.original} />
          ) : null,
      },
    ],
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- column defs stay stable; cells close over current values
    [],
  );

  const table = useReactTable<GroupUser>({
    data: filteredUsers,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div className="gap-5 flex w-full flex-col overflow-hidden">
      <div className="gap-5 flex items-center">
        <SearchField placeholder={m.search()} value={search} onValueChange={setSearch} />
        {import.meta.env.VITE_GROUPS_ACTIONS_ENABLED && (
          <UserDialog group={group}>
            <Button>
              {m.add()}
              <PlusIcon />
            </Button>
          </UserDialog>
        )}
      </div>
      <Table.Container>
        <Table.Root className="[&_thead]:top-0 table-fixed [&_thead]:sticky [&_thead]:z-1 [&_thead]:border-b-2 [&_thead]:border-primary-darker-gray [&_thead]:bg-primary-white">
          {table.getHeaderGroups().map(headergroup => (
            <React.Fragment key={headergroup.id}>
              <colgroup>
                <col />
                <col />
                <col />
                <col width={140} />
                <col />
                <col width={50} />
              </colgroup>
              <Table.Header
                key={headergroup.id}
                className="top-0 sticky z-1 border-b-2 border-primary-darker-gray bg-primary-white"
              >
                <Table.Row>
                  {headergroup.headers.map(header => (
                    <Table.Head key={header.id} className="pb-2.5" onClick={header.column.getToggleSortingHandler()}>
                      <div
                        className={cn(
                          "gap-2.5 [&_svg]:h-3 [&_svg]:w-3 flex items-center [&_svg]:text-primary-dark-gray",
                          header.column.getCanSort() && "cursor-pointer",
                        )}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: <ChevronUpIcon />,
                          desc: <ChevronDownIcon />,
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    </Table.Head>
                  ))}
                </Table.Row>
              </Table.Header>
            </React.Fragment>
          ))}
          <Table.Body>
            {table.getRowModel().rows.map(row => (
              <React.Fragment key={row.id}>
                <Table.Row className="border-b-2 border-primary-darker-gray last:border-b-0">
                  {row.getVisibleCells().map(cell => (
                    <Table.Cell key={cell.id} className="pt-5 pb-5">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Table.Cell>
                  ))}
                </Table.Row>
              </React.Fragment>
            ))}
          </Table.Body>
        </Table.Root>
      </Table.Container>
    </div>
  );
}
